import React, { useState, useEffect } from 'react';
import { Card, Spinner, Button, Badge } from 'react-bootstrap';
import './EmailDetail.css';

function EmailDetail({ emailId, apiBaseUrl }) {
  const [email, setEmail] = useState(null);
  const [emailBody, setEmailBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bodyType, setBodyType] = useState('text');

  useEffect(() => {
    if (emailId) {
      fetchEmailDetails(emailId);
    } else {
      // Reset when no email is selected
      setEmail(null);
      setEmailBody('');
      setError(null);
    }
  }, [emailId, apiBaseUrl]);

  const fetchEmailDetails = async (id) => {
    setLoading(true);
    try {
      // Fetch email metadata
      const metadataResponse = await fetch(`${apiBaseUrl}/api/emails/${id}`);
      if (!metadataResponse.ok) {
        throw new Error(`Failed to fetch email details: ${metadataResponse.statusText}`);
      }
      const metadata = await metadataResponse.json();
      setEmail(metadata);

      // Try to fetch HTML body first
      try {
        const htmlResponse = await fetch(`${apiBaseUrl}/api/emails/${id}/body.html`);
        if (htmlResponse.ok) {
          const htmlBody = await htmlResponse.text();
          setEmailBody(htmlBody);
          setBodyType('html');
          return;
        }
      } catch (err) {
        console.warn('HTML body not available, falling back to text');
      }

      // Fallback to text body
      try {
        const textResponse = await fetch(`${apiBaseUrl}/api/emails/${id}/body.txt`);
        if (textResponse.ok) {
          const textBody = await textResponse.text();
          setEmailBody(textBody);
          setBodyType('text');
          return;
        }
      } catch (err) {
        throw new Error('Failed to fetch email body');
      }

      setEmailBody('No content available');
      setBodyType('text');
    } catch (err) {
      setError(err.message);
      console.error('Error fetching email details:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString([], {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  if (!emailId) {
    return (
      <Card className="email-detail-empty">
        <Card.Body className="text-center p-5">
          <i className="bi bi-envelope-open text-muted" style={{ fontSize: '3rem' }}></i>
          <h4 className="mt-3">Select an email to view</h4>
          <p className="text-muted">Click on an email from the list to view its contents</p>
        </Card.Body>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="email-detail">
        <Card.Body className="text-center p-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading email content...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="email-detail">
        <Card.Body className="text-center p-5">
          <i className="bi bi-exclamation-triangle text-danger" style={{ fontSize: '2rem' }}></i>
          <h5 className="mt-3">Error</h5>
          <p>{error}</p>
          <Button 
            variant="primary" 
            size="sm" 
            onClick={() => fetchEmailDetails(emailId)}
          >
            Try Again
          </Button>
        </Card.Body>
      </Card>
    );
  }

  if (!email) {
    return null;
  }

  return (
    <Card className="email-detail">
      <Card.Header>
        <h4>{email.subject || '(No Subject)'}</h4>
        <div className="email-metadata">
          <div className="sender-info">
            <div><strong>From:</strong> {email.from || 'Unknown'}</div>
            <div><strong>To:</strong> {email.to || 'Unknown'}</div>
          </div>
          <div className="email-time">{formatDate(email.date)}</div>
        </div>
      </Card.Header>
      <Card.Body>
        {bodyType === 'html' ? (
          <div 
            className="email-html-body"
            dangerouslySetInnerHTML={{ __html: emailBody }}
          />
        ) : (
          <pre className="email-text-body">{emailBody}</pre>
        )}

        {email.attachments && email.attachments.length > 0 && (
          <div className="email-attachments mt-4">
            <h6>
              <i className="bi bi-paperclip me-1"></i>
              Attachments ({email.attachments.length})
            </h6>
            <div className="attachment-list">
              {email.attachments.map((attachment, index) => (
                <div key={index} className="attachment-item">
                  <i className="bi bi-file-earmark me-2"></i>
                  <a 
                    href={`${apiBaseUrl}/api/emails/${emailId}/attachments/${attachment.index}`}
                    download={attachment.filename}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {attachment.filename}
                    {attachment.size && (
                      <Badge bg="light" text="dark" className="ms-2">
                        {formatFileSize(attachment.size)}
                      </Badge>
                    )}
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default EmailDetail;
